import { useState, useMemo, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Package, X, ShoppingCart, Briefcase, Gift, Zap, Star, Tag, Box, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useBrandProductsHooks } from '@/hooks/useBrandProductsHooks';

interface ProductSearchComboboxProps {
  brandId: string;
  value: string | null;
  onChange: (productId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

// Icon mapping based on product type
const getTypeIcon = (type: string) => {
  const typeIcons: Record<string, React.ElementType> = {
    'Product': ShoppingCart,
    'Service': Briefcase,
    'Offer': Gift,
    'Feature': Zap,
    'Bundle': Box,
    'Subscription': Star,
    'Promo': Tag,
  };
  return typeIcons[type] || Package;
};

// Funnel stage color mapping
const getFunnelStageColor = (stage: string) => {
  const stageColors: Record<string, string> = {
    'Awareness': 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
    'Interest': 'bg-purple-500/20 text-purple-700 dark:text-purple-300',
    'Consideration': 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
    'Intent': 'bg-orange-500/20 text-orange-700 dark:text-orange-300',
    'Evaluation': 'bg-pink-500/20 text-pink-700 dark:text-pink-300',
    'Purchase': 'bg-green-500/20 text-green-700 dark:text-green-300',
    'Retention': 'bg-teal-500/20 text-teal-700 dark:text-teal-300',
    'Advocacy': 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300',
  };
  return stageColors[stage] || 'bg-muted text-muted-foreground';
};

export function ProductSearchCombobox({
  brandId,
  value,
  onChange,
  placeholder = 'Select a product...',
  disabled = false,
}: ProductSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: products = [], isLoading } = useBrandProductsHooks(brandId);

  // Sort and filter products
  const filteredProducts = useMemo(() => {
    const sorted = [...products].sort((a, b) => 
      a.product.toLowerCase().localeCompare(b.product.toLowerCase())
    );
    
    if (!search.trim()) return sorted;
    
    const searchLower = search.toLowerCase();
    return sorted.filter(p => 
      p.product.toLowerCase().includes(searchLower) ||
      p.type.toLowerCase().includes(searchLower) ||
      p.funnel_stage.toLowerCase().includes(searchLower)
    );
  }, [products, search]);

  const selectedProduct = products.find((p) => p.id === value);
  const SelectedIcon = selectedProduct ? getTypeIcon(selectedProduct.type) : Package;

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleSelect = (productId: string | null) => {
    onChange(productId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        disabled={disabled || isLoading}
        onClick={() => setOpen(!open)}
        className="w-full justify-between"
      >
        {isLoading ? (
          <span className="text-muted-foreground">Loading...</span>
        ) : selectedProduct ? (
          <div className="flex items-center gap-2 truncate">
            <SelectedIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{selectedProduct.product}</span>
            <Badge variant="secondary" className="ml-1 shrink-0">
              {selectedProduct.type}
            </Badge>
            <Badge className={cn("ml-1 shrink-0 border-0", getFunnelStageColor(selectedProduct.funnel_stage))}>
              {selectedProduct.funnel_stage}
            </Badge>
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute top-full left-0 right-0 z-[99999] mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-lg">
          {/* Search input */}
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="h-10 border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          {/* Options list */}
          <div className="max-h-[300px] overflow-y-auto p-1">
            {/* Clear option */}
            <div
              onClick={() => handleSelect(null)}
              className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
            >
              <X className="mr-2 h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">No product linked</span>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No products found.
              </div>
            ) : (
              filteredProducts.map((product) => {
                const TypeIcon = getTypeIcon(product.type);
                return (
                  <div
                    key={product.id}
                    onClick={() => handleSelect(product.id)}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === product.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex items-center gap-2 truncate">
                      <TypeIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{product.product}</span>
                      <Badge variant="secondary" className="ml-1 shrink-0">
                        {product.type}
                      </Badge>
                      <Badge className={cn("ml-1 shrink-0 border-0", getFunnelStageColor(product.funnel_stage))}>
                        {product.funnel_stage}
                      </Badge>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
